/**
 * P5 管理控制台配置项中文标签 + 业务说明
 *
 * 集中映射 lifecycle_config / system_configs 的 key → 用户视角的中文。
 * 术语主语统一：
 *   销售员做的事 → 主语写"销售员..."
 *   客户状态/属性 → 主语写"客户..."
 *   主管做的事   → 主语写"主管..."
 *   系统/流程     → 业务描述，无主语
 */

export type ConfigGroup = 'ai' | 'business' | 'finance' | 'data' | 'code' | 'test' | 'wework' | 'rbac' | 'sms' | 'auth_2fa' | 'monitor' | 'fadada' | 'salary';

export interface ConfigLabel {
  /** 后端 key（lifecycle_config.config_key 或 system_configs.config_key） */
  key: string;
  /** 源表 — 决定改 PATCH 走 /lifecycle-config 还是 /admin/system-configs */
  source: 'lifecycle' | 'system';
  /** 分组 */
  group: ConfigGroup;
  /** icon emoji */
  icon: string;
  /** 中文标题（用户视角，主语统一） */
  title: string;
  /** 业务说明（讲清谁做什么、影响什么） */
  description: string;
  /** 单位（天/小时/字/元/% 等） */
  unit?: string;
  /** 预览函数（如有）— 接收 value，返回示例字符串 */
  preview?: (value: string) => string;
}

export const ADMIN_CONFIG_LABELS: ConfigLabel[] = [
  /* ─── AI 设置 ─── */
  {
    key: 'ai.monthly_total_budget_cny', source: 'system', group: 'ai', icon: '💰',
    title: 'AI 月度总预算',
    description: 'CRM 系统每月调用 AI 接口的总预算上限（人民币）',
    unit: '元',
  },
  {
    key: 'ai.budget_warning_pct', source: 'system', group: 'ai', icon: '⚠️',
    title: 'AI 预算预警百分比',
    description: '当月 AI 调用费用达到预算的此百分比时发送预警',
    unit: '%',
  },

  /* ─── 业务规则 - 客户加微信组（销售员视角） ─── */
  {
    key: 'l0_grace_hours', source: 'lifecycle', group: 'business', icon: '⏰',
    title: '销售员添加客户微信宽限期',
    description: '销售员收到客户分配后必须在此时间内成功添加客户微信，超时后销售员可申请作废或客户自动进流失公海',
    unit: '小时',
  },
  {
    key: 'l0_attempt_threshold', source: 'lifecycle', group: 'business', icon: '🔄',
    title: '销售员添加微信最少尝试次数',
    description: '销售员未加上客户微信前必须尝试此次数以上才能申请作废，避免随意放弃客户',
    unit: '次',
  },
  {
    key: 'l0_voidage_review_hours', source: 'lifecycle', group: 'business', icon: '⏳',
    title: '主管审批添加失败作废超时',
    description: '销售员申请客户作废后主管必须在此时间内审批；超时自动通过，避免审批拖延',
    unit: '小时',
  },
  {
    key: 'pool.add_wechat_timeout_hours', source: 'system', group: 'business', icon: '⏰',
    title: '公海客户接收后加微信超时',
    description: '销售员从公海抢到客户后必须在此时间内加上客户微信',
    unit: '小时',
  },
  {
    key: 'reserve_pool_claim_protect_hours', source: 'lifecycle', group: 'business', icon: '🔒',
    title: '储备公海认领保护期',
    description: '销售员从储备公海抢到客户后此时间内不会被回收，给销售员稳定跟进窗口',
    unit: '小时',
  },

  /* ─── 业务规则 - 客户阶段组（客户状态视角） ─── */
  {
    key: 'l1_grace_days', source: 'lifecycle', group: 'business', icon: '🌱',
    title: 'L1 触达阶段宽限期',
    description: '销售员加上客户微信后此天数内客户必须开口（破冰），否则客户进流失公海',
    unit: '天',
  },
  {
    key: 'l1_plus_l2_grace_days', source: 'lifecycle', group: 'business', icon: '🌿',
    title: 'L1+L2 累计宽限期',
    description: '从客户分配开始累计此天数客户必须进入 L3 挖需阶段，否则进流失公海',
    unit: '天',
  },
  {
    key: 'cycle_days', source: 'lifecycle', group: 'business', icon: '🔁',
    title: '客户跟进统计周期',
    description: '每此天数作为一个跟进考核周期，周期内跟进天数不达标客户进流失公海',
    unit: '天',
  },
  {
    key: 'min_followup_days', source: 'lifecycle', group: 'business', icon: '📅',
    title: '周期内销售员最少跟进天数',
    description: '销售员必须在每个考核周期内至少跟进客户此天数，否则客户进流失公海',
    unit: '天',
  },
  {
    key: 'l7_grace_days', source: 'lifecycle', group: 'business', icon: '🎓',
    title: 'L7 成交客户跟服周期',
    description: '成交客户每此天数跟进一次（服务期低频跟进）',
    unit: '天',
  },
  {
    key: 'l8_grace_days', source: 'lifecycle', group: 'business', icon: '🎁',
    title: 'L8 服务期客户回访周期',
    description: '服务期客户每此天数回访一次（季度回访制）',
    unit: '天',
  },

  /* ─── 业务规则 - 跟进监管组 ─── */
  {
    key: 'l0_next_contact_hours', source: 'lifecycle', group: 'business', icon: '⏰',
    title: 'L0 下次沟通延后小时（系统自动算）',
    description: '销售员保存 L0 跟进后，系统自动把"下次沟通时间"设为此小时数后，员工不可改（任务 C2）',
    unit: '小时',
  },
  {
    key: 'l1_next_contact_hours', source: 'lifecycle', group: 'business', icon: '⏰',
    title: 'L1 下次沟通延后小时（系统自动算）',
    description: '销售员保存 L1 跟进后，系统自动把"下次沟通时间"设为此小时数后，员工不可改（任务 C2）',
    unit: '小时',
  },
  {
    key: 'l2_next_contact_hours', source: 'lifecycle', group: 'business', icon: '⏰',
    title: 'L2 下次沟通延后小时（系统自动算）',
    description: '销售员保存 L2 跟进后，系统自动把"下次沟通时间"设为此小时数后，员工不可改（任务 C2）',
    unit: '小时',
  },
  {
    key: 'pool_recycle_window_days', source: 'lifecycle', group: 'business', icon: '🔁',
    title: '公海回收滚动窗口',
    description: '每日 cron 检查客户最近此天数内的跟进天数（任务 C3）',
    unit: '天',
  },
  {
    key: 'pool_recycle_min_follow_days', source: 'lifecycle', group: 'business', icon: '📅',
    title: '公海回收最少跟进天数',
    description: '滚动窗口内 distinct 跟进日期少于此值的客户将被回收到公海（任务 C3）',
    unit: '天',
  },
  {
    key: 'pool_recycle_cron_hour', source: 'lifecycle', group: 'business', icon: '🕑',
    title: '公海回收 cron 运行小时',
    description: '每天此小时（0-23）运行公海回收 job，默认凌晨 2 点（任务 C3）',
    unit: '小时',
  },
  {
    key: 'l1_no_followup_alert_hours', source: 'lifecycle', group: 'business', icon: '🚨',
    title: 'L1 阶段无跟进告警时长',
    description: '客户进入 L1 后此小时数销售员未跟进，主管收钉钉告警',
    unit: '小时',
  },
  {
    key: 'l2_no_followup_alert_days', source: 'lifecycle', group: 'business', icon: '🚨',
    title: 'L2 阶段无跟进告警时长',
    description: '客户进入 L2 后此天数销售员未跟进，主管收钉钉告警',
    unit: '天',
  },
  {
    key: 'l3_followup_min_chars', source: 'lifecycle', group: 'business', icon: '📝',
    title: 'L3 阶段销售员跟进内容最少字数',
    description: '销售员在 L3 挖需阶段及之后的每条跟进内容不能少于此字数',
    unit: '字',
  },
  {
    key: 'l3_pain_point_min_chars', source: 'lifecycle', group: 'business', icon: '💡',
    title: 'L3 客户痛点描述最少字数',
    description: '销售员录入的 L3 客户痛点不能少于此字数（与跟进内容分开校验）',
    unit: '字',
  },
  {
    key: 'followup.content_min_length', source: 'system', group: 'business', icon: '📝',
    title: '跟进内容最少字数（通用）',
    description: '所有阶段跟进内容的通用最少字数要求',
    unit: '字',
  },
  {
    key: 'followup.stage_stale_days', source: 'system', group: 'business', icon: '⏸',
    title: '阶段停滞禁止提交天数',
    description: '客户在某阶段停滞超过此天数销售员需先推进阶段才能继续跟进',
    unit: '天',
  },

  /* ─── 业务规则 - 公海组 ─── */
  {
    key: 'pool.cooldown_days', source: 'system', group: 'business', icon: '❄️',
    title: '公海冷却天数',
    description: '客户进公海后此天数内不能被原销售员抢回，保证流转公平',
    unit: '天',
  },
  {
    key: 'pool_screenshot_grace_days', source: 'lifecycle', group: 'business', icon: '📸',
    title: '进公海后截图上传期限',
    description: '销售员的客户进流失公海后必须此天数内上传完整聊天长截图，否则销售员绩效扣分',
    unit: '天',
  },
  {
    key: 'pool.assignment_timeout_minutes', source: 'system', group: 'business', icon: '⏱',
    title: '派单接收倒计时',
    description: '主管派单给销售员后销售员必须此分钟数内接收，否则系统重新派给其他人',
    unit: '分钟',
  },
  {
    key: 'pool.first_followup_timeout_hours', source: 'system', group: 'business', icon: '🚦',
    title: '首次跟进倒计时',
    description: '销售员接收客户后此小时数内必须首次跟进，否则触发告警',
    unit: '小时',
  },
  {
    key: 'pool.grab_limit_regular', source: 'system', group: 'business', icon: '🎣',
    title: '正式销售员每日抢单上限',
    description: '正式销售员每天从公海抢单的最大数量',
    unit: '单',
  },
  {
    key: 'pool.grab_limit_probation', source: 'system', group: 'business', icon: '🐣',
    title: '试用期销售员每日抢单上限',
    description: '试用期销售员每天从公海抢单的最大数量（通常小于正式）',
    unit: '单',
  },
  {
    key: 'pool.grab_open_hours', source: 'system', group: 'business', icon: '🕐',
    title: '公海开放抢单时段',
    description: '一天中允许抢单的小时区间（如 "09-18" 表示 9 点到 18 点）',
  },
  {
    key: 'pool.silent_days', source: 'system', group: 'business', icon: '🤫',
    title: '沉默池进入条件',
    description: '客户连续多少天无跟进且未成交进入沉默池',
    unit: '天',
  },

  /* ─── 财务规则 ─── */
  {
    key: 'contract.expire_reminder_days', source: 'system', group: 'finance', icon: '📅',
    title: '合同到期提醒梯度',
    description: '合同到期前在以下天数节点发送提醒（JSON 数组）',
  },
  {
    key: 'payment.overdue_reminder_days', source: 'system', group: 'finance', icon: '💸',
    title: '收款逾期催收梯度',
    description: '收款逾期在以下天数节点发送催收钉钉提醒（JSON 数组）',
  },
  {
    key: 'approval.order_amount_threshold', source: 'system', group: 'finance', icon: '🏦',
    title: '订单金额走总监审批阈值',
    description: '订单金额超过此值必须走 director 审批',
    unit: '元',
  },
  // 评审#10：「默认订单提成比例」已废弃——薪酬阶段1 起订单提成改由「薪酬配置→提成矩阵[职级×班型]」决定，
  //   order.commission_rate 不再驱动算钱（仅 order 旧字段反推记录）。删除该可编辑卡，避免 admin 误改无效项。
  //   (DB 里 order.commission_rate 系统配置行保留无害；前端不再渲染该卡)

  /* ─── 数据 & 通知 ─── */
  {
    key: 'notification.dingtalk_rate_limit_per_minute', source: 'system', group: 'data', icon: '🔕',
    title: '钉钉单用户每分钟通知上限',
    description: '防止钉钉消息轰炸，每个用户每分钟最多收此条数通知',
    unit: '条',
  },
  {
    key: 'health.poor_threshold', source: 'system', group: 'data', icon: '🔴',
    title: '客户健康度危险阈值',
    description: '客户健康度低于此值会触发危险告警（红色）',
    unit: '分',
  },
  {
    key: 'health.warning_threshold', source: 'system', group: 'data', icon: '🟡',
    title: '客户健康度警告阈值',
    description: '客户健康度低于此值会触发警告（黄色）',
    unit: '分',
  },

  /* ─── 客户编号 ─── */
  {
    key: 'internal_code_prefix', source: 'lifecycle', group: 'code', icon: '🏷️',
    title: '客户内部编号前缀',
    description: '系统为新客户自动生成的内部编号前缀（默认 KJ）',
    preview: (v) => `${v}+20260520+张momo`,
  },
  {
    key: 'internal_code_date_format', source: 'lifecycle', group: 'code', icon: '📆',
    title: '客户内部编号日期格式',
    description: '客户编号中日期部分的格式（可选 YYYYMMDD / YYMMDD / MMDD）',
    preview: (v) => {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const yy = String(yyyy).slice(2);
      const date = v === 'YYMMDD' ? `${yy}${mm}${dd}`
                  : v === 'MMDD' ? `${mm}${dd}`
                  : `${yyyy}${mm}${dd}`;
      return `KJ+${date}+张momo`;
    },
  },
];

export const GROUP_META: Record<ConfigGroup, { label: string; icon: string; desc: string }> = {
  ai:       { label: 'AI 设置',     icon: '🤖', desc: 'AI 调用预算与功能开关' },
  business: { label: '业务规则',    icon: '📋', desc: '客户阶段、跟进规则、公海制度' },
  finance:  { label: '财务规则',    icon: '💰', desc: '合同 / 收款 / 订单审批阈值' },
  data:     { label: '数据 & 通知', icon: '📊', desc: '健康度阈值与通知频次' },
  code:     { label: '客户编号',    icon: '🏷️', desc: '内部编号生成规则' },
  test:     { label: '测试中心',    icon: '🧪', desc: '手动触发 cron / 事件（仅开发环境）' },
  wework:   { label: '企微会话存档', icon: '💬', desc: '企业微信会话留存配置 + 拉取测试 + 日志（v1.2 ⑥）' },
  rbac:     { label: '权限管理',     icon: '🔐', desc: '改角色权限点 / 用户级权限覆盖 / 查看审计日志（v1.5 阶段 5）' },
  sms:      { label: '短信配置',     icon: '📱', desc: '阿里云短信 AK/SK/签名/模板 + 测试发送（v1.5）' },
  auth_2fa: { label: '2FA 设置',     icon: '🔐', desc: '双因素认证开关 / 角色范围 / 白名单（v1.5）' },
  monitor:  { label: '系统监控',     icon: '📊', desc: 'Cron 健康度 / AI 月度消耗 / SMS 用量 / 今日活跃（v1.5 P0 B6）' },
  fadada:   { label: '电子签',       icon: '✍️', desc: '法大大 AppId/AppSecret/域名 + 测连接（B1-cfg；密文存专表，token 走 Redis）' },
  salary:   { label: '薪酬配置',     icon: '💴', desc: '班型定死价 / 职级底薪 / 提成矩阵（薪酬阶段1；改矩阵升版本+二次确认）' },
};

/** 找一个 key 的 label 元数据，未配置时给出 fallback */
export function findLabel(key: string): ConfigLabel | null {
  return ADMIN_CONFIG_LABELS.find((l) => l.key === key) ?? null;
}
